import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';

import {
  of,
  tap,
  filter,
  Subject,
  concatMap,
  switchMap,
  catchError,
  Subscription,
  combineLatest, take,
} from 'rxjs';

import { BookingOptions } from '@shared/restaurant/models';
import { Service, UpdateServiceBulkRequest } from '@shared/service/models';
import { AuthUser } from '@shared/signup/models';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
} from '@tablesready/common-ui';
import {
  ServiceApiService,
  ServicesService,
  WhoService,
} from '../../../../services';
import { AddEditServiceComponent } from '../modals/add-edit-service/add-edit-service.component';
import { repeat } from 'rxjs/operators';
import { Schedule } from '@shared/schedule/models';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';

interface ServiceUpdateParams {
  id: string;
  enabled: boolean;
}

@Component({
  selector: 'tr-services-list',
  styleUrls: ['./services-list.component.scss'],
  templateUrl: './services-list.component.html',
})
export class ServicesListComponent implements OnInit, OnDestroy {
  icon = AppIcon;
  color = AppIconColor;
  size = AppIconSize;

  @Input() serviceSettings;
  @Input() reservationsLabel;
  @Input() availableSchedules: Schedule[] = [];
  @Input() bookingOptions: BookingOptions;
  @Input() isUserOnFreePlan: boolean;
  @Input() allowCharge = false;

  showCategories = false;
  isReorder = false;
  services: Service[] = [];
  servicesCategories = new Set();
  newService = {};
  who: AuthUser;

  private readonly updateServiceById$ = new Subject<ServiceUpdateParams>()
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly servicesService: ServicesService,
    private readonly alertsService: AlertsService,
    private readonly modalController: FrontendModalService,
    private readonly whoService: WhoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly serviceApiService: ServiceApiService,
  ) { }

  ngOnInit(): void {
    const subscription = combineLatest([
      this.authRestaurantService.select(),
      this.servicesService.selectAllSorted(),
    ])
      .pipe(
        tap(([user, services]) => {
          this.who = user;
          this.serviceSettings = user.location.serviceSettings;
          this.services = services;

          const sort = this.serviceSettings?.sort || 'order';
          this.showCategories = sort === 'category';
          this.isReorder = sort === 'category' || sort === 'name';
          this.createdCategories();
          this.cdr.detectChanges();
        }),
        catchError(error => {
          this.alertsService.addError(`${error}`);

          return of(null);
        }),
      )
      .subscribe();

    const updateSub = this.updateServiceById$
      .pipe(
        concatMap((data) => {
          const { id, enabled } = data;

          return this.serviceApiService.updateById(id, { enabled });
        }),
        tap((service) => {
          this.alertsService.addSuccess(`Service ${service.enabled ? 'enabled' : 'disabled'}`, 'Success:');
        }),
        catchError((err) => {
          this.alertsService.addError(err?.message, 'Error:');

          return err;
        }),
        repeat(),
      )
      .subscribe();

    this.subscriptions.add(updateSub);
    this.subscriptions.add(subscription);
  }

  createdCategories() {
    this.services.forEach(service => {
      if (service.category && service.category.trim() !== '') {
        this.servicesCategories.add(service.category);
      }
    });
  }

  addEditService(service = {}) {
    this.modalController.create<AddEditServiceComponent, Service>(AddEditServiceComponent, {
      service,
      count: this.services.length,
      availableSchedules: this.availableSchedules,
      categories: Array.from(this.servicesCategories),
      bookingOptions: this.bookingOptions,
      publicUrl: this.who?.location.publicUrl,
      isUserOnFreePlan: this.isUserOnFreePlan,
      stripeEnabled: (this.allowCharge &&
        this.who.account.features.paymentAccepting &&
        this.who.location.integrations?.stripe?.enabled) ||
        false,
    })
      .pipe(
        take(1),
        filter(data => !!data),
      )
      .subscribe((res) => {
        if (!res._id) {
          this.saveService(res);
          return;
        }

        this.updateService(res);
      });
  }

  getScheduleName(scheduleId: string): string {
    if (!scheduleId || !this.availableSchedules) {
      return '';
    }
    return this.availableSchedules.find(s => s._id === scheduleId)?.name || '';
  }

  updateService(service: Service): void {
    const {_id, ...rest} = service;
    this.serviceApiService.updateById(_id, rest).subscribe(
      (response) => {
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Updated Service',
          body: '',
        });

        this.services = this.services.map((item) => {
          if (item._id === service._id) {
            return { ...response };
          }

          return item;
        });
        this.cdr.detectChanges();
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: error.error.error,
        });
      }
    );
  }

  saveService(service) {
    this.newService = service;
    this.serviceApiService.create(service)
      .pipe(
        take(1),
        tap(() => {
          this.newService = {};
        }),
        catchError((error) => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: error.error.message,
          });

          return of(error);
        }),
      )
      .subscribe();
  }

  deleteService(service: Service): void {
    if (!service._id) {
      this.alertsService.addError('Service not yet saved. Nothing to delete.');
    } else {
      this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
        options: {
          title: `Delete ${this.serviceSettings.label.toUpperCase()}`,
          content: `Are you sure you want to delete ${service.name}?`,
        },
      })
        .pipe(
          take(1),
          filter((res) => res?.response),
          switchMap(() => this.serviceApiService.deleteById(service._id)),
          tap(() => {
            this.services = this.services.filter(item => item._id !== service._id);
            this.cdr.detectChanges();
            this.alertsService.addSuccess(service.name, 'Deleted: ');
          }),
          catchError(error => {
            this.alertsService.addError(error);
            return of(null);
          }),
        )
        .subscribe();
    }
  }

  toggleEnabled(enabled: boolean, id: string): void {
    if (!id) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Error:',
        body: 'Service not yet saved. Nothing to toggle.',
      });

      return;
    }

    this.updateServiceById$.next({ id, enabled });
  }

  move(pos, move) {
    this.array_move(this.services, pos, pos + move);
    const updates: UpdateServiceBulkRequest[] = [];
    for (let i = 0; i < this.services.length; i++) {
      this.services[i].order = i;
      updates.push({
        _id: this.services[i]._id,
        order: i,
      });
    }

    if (this.serviceSettings.sortByCategory) {
      this.whoService.updateServiceSettings({ sortByCategory: false });
    }

    this.serviceApiService.updateBatch(updates).subscribe(
      () => {},
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: error,
        });
      }
    )
  }

  array_move(arr, old_index, new_index) {
    if (new_index >= arr.length) {
      let k = new_index - arr.length + 1;
      while (k--) {
        // eslint-disable-next-line no-undefined
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  trackBy(index: number): number {
    return index;
  }
}
